import { directSupabase } from '../../../lib/supabaseClient';
import type { PdfExportConfig, PdfExportConfigInput } from '../types/pdfExportConfig.types';
import type { TemplateKind } from '../types/documents.types';

function fromDbConfig(row: any): PdfExportConfig {
  return {
    id: row.id,
    organisationId: row.organisation_id,
    documentType: row.document_type,
    companyName: row.company_name,
    companyTradingName: row.company_trading_name,
    companyUtr: row.company_utr,
    companyAddress: row.company_address,
    companyPhone: row.company_phone,
    companyEmail: row.company_email,
    companyVatNumber: row.company_vat_number,
    companyRegistrationNumber: row.company_registration_number,
    bankName: row.bank_name,
    bankAccountName: row.bank_account_name,
    bankAccountNumber: row.bank_account_number,
    bankSortCode: row.bank_sort_code,
    bankIban: row.bank_iban,
    bankSwift: row.bank_swift,
    logoPath: row.logo_path,
    logoWidth: row.logo_width,
    logoHeight: row.logo_height,
    headerImagePath: row.header_image_path,
    headerTitle: row.header_title,
    headerSubtitle: row.header_subtitle,
    headerColor: row.header_color,
    footerImagePath: row.footer_image_path,
    footerText: row.footer_text,
    showGenerationDate: row.show_generation_date,
    showPageNumbers: row.show_page_numbers,
    primaryColor: row.primary_color,
    secondaryColor: row.secondary_color,
    fontFamily: row.font_family,
    fontSizeBase: row.font_size_base,
    businessRegisteredAddress: row.business_registered_address,
    businessWebsite: row.business_website,
    businessTerms: row.business_terms,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toDbConfig(input: PdfExportConfigInput | Partial<PdfExportConfigInput>): any {
  const payload: any = {};
  
  if (input.organisationId !== undefined) payload.organisation_id = input.organisationId;
  if (input.documentType !== undefined) payload.document_type = input.documentType;
  if (input.companyName !== undefined) payload.company_name = input.companyName;
  if (input.companyTradingName !== undefined) payload.company_trading_name = input.companyTradingName;
  if (input.companyUtr !== undefined) payload.company_utr = input.companyUtr;
  if (input.companyAddress !== undefined) payload.company_address = input.companyAddress;
  if (input.companyPhone !== undefined) payload.company_phone = input.companyPhone;
  if (input.companyEmail !== undefined) payload.company_email = input.companyEmail;
  if (input.companyVatNumber !== undefined) payload.company_vat_number = input.companyVatNumber;
  if (input.companyRegistrationNumber !== undefined) payload.company_registration_number = input.companyRegistrationNumber;
  if (input.bankName !== undefined) payload.bank_name = input.bankName;
  if (input.bankAccountName !== undefined) payload.bank_account_name = input.bankAccountName;
  if (input.bankAccountNumber !== undefined) payload.bank_account_number = input.bankAccountNumber;
  if (input.bankSortCode !== undefined) payload.bank_sort_code = input.bankSortCode;
  if (input.bankIban !== undefined) payload.bank_iban = input.bankIban;
  if (input.bankSwift !== undefined) payload.bank_swift = input.bankSwift;
  if (input.logoPath !== undefined) payload.logo_path = input.logoPath;
  if (input.logoWidth !== undefined) payload.logo_width = input.logoWidth;
  if (input.logoHeight !== undefined) payload.logo_height = input.logoHeight;
  if (input.headerImagePath !== undefined) payload.header_image_path = input.headerImagePath;
  if (input.headerTitle !== undefined) payload.header_title = input.headerTitle;
  if (input.headerSubtitle !== undefined) payload.header_subtitle = input.headerSubtitle;
  if (input.headerColor !== undefined) payload.header_color = input.headerColor;
  if (input.footerImagePath !== undefined) payload.footer_image_path = input.footerImagePath;
  if (input.footerText !== undefined) payload.footer_text = input.footerText;
  if (input.showGenerationDate !== undefined) payload.show_generation_date = input.showGenerationDate;
  if (input.showPageNumbers !== undefined) payload.show_page_numbers = input.showPageNumbers;
  if (input.primaryColor !== undefined) payload.primary_color = input.primaryColor;
  if (input.secondaryColor !== undefined) payload.secondary_color = input.secondaryColor;
  if (input.fontFamily !== undefined) payload.font_family = input.fontFamily;
  if (input.fontSizeBase !== undefined) payload.font_size_base = input.fontSizeBase;
  if (input.businessRegisteredAddress !== undefined) payload.business_registered_address = input.businessRegisteredAddress;
  if (input.businessWebsite !== undefined) payload.business_website = input.businessWebsite;
  if (input.businessTerms !== undefined) payload.business_terms = input.businessTerms;
  
  return payload;
}

export const pdfExportConfigService = {
  async getConfigByOrganisation(organisationId: string, documentType: TemplateKind): Promise<PdfExportConfig | null> {
    const { data, error } = await directSupabase
      .from('pdf_export_config')
      .select('*')
      .eq('organisation_id', organisationId)
      .eq('document_type', documentType)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - return null
        return null;
      }
      throw new Error(error.message);
    }
    
    return fromDbConfig(data);
  },

  async createConfig(input: PdfExportConfigInput): Promise<PdfExportConfig> {
    const payload = toDbConfig(input);
    
    const { data, error } = await directSupabase
      .from('pdf_export_config')
      .insert(payload)
      .select('*')
      .single();
    
    if (error) throw new Error(error.message);
    return fromDbConfig(data);
  },

  async updateConfig(id: string, input: Partial<PdfExportConfigInput>): Promise<PdfExportConfig> {
    const payload = toDbConfig(input);
    
    const { data, error } = await directSupabase
      .from('pdf_export_config')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single();
    
    if (error) throw new Error(error.message);
    return fromDbConfig(data);
  },

  async upsertConfig(input: PdfExportConfigInput): Promise<PdfExportConfig> {
    try {
      console.log('Upserting config with input:', input);
      
      // Try to get existing config
      const existing = await this.getConfigByOrganisation(input.organisationId, input.documentType);
      console.log('Existing config found:', existing);
      
      if (existing) {
        // Update existing
        console.log('Updating existing config...');
        return await this.updateConfig(existing.id, input);
      } else {
        // Create new
        console.log('Creating new config...');
        return await this.createConfig(input);
      }
    } catch (error) {
      console.error('Error in upsertConfig:', error);
      throw new Error(`Failed to upsert PDF export config: ${error}`);
    }
  },

  async getAllConfigs(): Promise<PdfExportConfig[]> {
    const { data, error } = await directSupabase
      .from('pdf_export_config')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw new Error(error.message);
    return (data || []).map(fromDbConfig);
  },

  async getConfigsByOrganisation(organisationId: string): Promise<PdfExportConfig[]> {
    const { data, error } = await directSupabase
      .from('pdf_export_config')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('document_type', { ascending: true });
    
    if (error) throw new Error(error.message);
    return (data || []).map(fromDbConfig);
  },

  async deleteConfig(id: string): Promise<void> {
    const { error } = await directSupabase
      .from('pdf_export_config')
      .delete()
      .eq('id', id);
    
    if (error) throw new Error(error.message);
  }
};
